/*
 * Arctic Spas Referral Program — front-end logic
 *
 * Used by:
 *   - page.referral.liquid           (signup landing page)
 *   - page.referral-dashboard.liquid (referrer dashboard with magic-link auth)
 *   - snippets/referral-tracker.liquid (site-wide cookie tracker)
 *
 * Talks to Supabase via the public anon key. All sensitive operations go
 * through SECURITY DEFINER RPC functions defined in migration 004.
 */

(function () {
  'use strict';

  // -------------------- Configuration --------------------

  const SUPABASE_URL = 'https://cskufwwwcytnhhjmtyya.supabase.co';
  const SUPABASE_ANON_KEY = 'sb_publishable_S-O4covVApNyWlILAyjGgw_XIRkVdkS';

  const COOKIE_NAME = 'as_ref';
  const COOKIE_DAYS = 90;
  const STORAGE_AUTH_KEY = 'as-referral-auth';

  // -------------------- Cookie helpers --------------------

  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      ';expires=' +
      d.toUTCString() +
      ';path=/;samesite=lax';
  }

  function getCookie(name) {
    const parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    return null;
  }

  function clearCookie(name) {
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;samesite=lax';
  }

  // -------------------- Referral code checks --------------------

  /**
   * Cheap client-side pre-filter for a ?ref= value, so obviously foreign
   * values (Judge.me's "judge.me", cro.media's "cro.media" - both contain a
   * dot) never reach the RPC. Deliberately loose: real codes are
   * generate_referral_code()'s "ABC-XXXX" (letters, digits, and the base64
   * '+' / '/'), and this must never reject one of those. The server-side
   * referrers lookup in referral_record_click stays the authority.
   *
   * @param {unknown} code
   * @returns {boolean}
   */
  function isPlausibleCode(code) {
    if (typeof code !== 'string') return false;
    return /^[A-Za-z0-9+\/-]{3,20}$/.test(code.trim());
  }

  /**
   * True when the click RPC said the code matches no referrer: the
   * post-202610112330 {success:false, error:'invalid_code'} or the older
   * {success:true, valid_code:false}. 'no_code' counts too.
   *
   * @param {any} res parsed RPC response
   * @returns {boolean}
   */
  function isRejectedCode(res) {
    if (!res || typeof res !== 'object') return false;
    if (res.valid_code === false) return true;
    return res.error === 'invalid_code' || res.error === 'no_code';
  }

  function genCookieId() {
    const a = new Uint8Array(16);
    crypto.getRandomValues(a);
    return Array.from(a)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // -------------------- Supabase REST helpers --------------------

  function rpc(fnName, body) {
    return fetch(SUPABASE_URL + '/rest/v1/rpc/' + fnName, {
      method: 'POST',
      headers: rpcHeaders(),
      body: JSON.stringify(body || {}),
    }).then(handleResponse);
  }

  function rpcHeaders() {
    const h = {
      apikey: SUPABASE_ANON_KEY,
      Authorization: 'Bearer ' + (getAccessToken() || SUPABASE_ANON_KEY),
      'Content-Type': 'application/json',
    };
    return h;
  }

  function handleResponse(resp) {
    return resp.text().then((text) => {
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = text;
      }
      if (!resp.ok) {
        const err = new Error(
          'Supabase ' + resp.status + ': ' + (data && data.message ? data.message : text)
        );
        err.status = resp.status;
        err.body = data;
        throw err;
      }
      return data;
    });
  }

  // -------------------- Auth (magic link) --------------------

  function loadAuth() {
    try {
      const raw = localStorage.getItem(STORAGE_AUTH_KEY);
      if (!raw) return null;
      const a = JSON.parse(raw);
      if (a && a.expires_at && Date.now() / 1000 < a.expires_at) return a;
      // expired — try refresh
      if (a && a.refresh_token) return a; // refresh handled lazily below
    } catch (e) {}
    return null;
  }

  function saveAuth(session) {
    if (!session) {
      localStorage.removeItem(STORAGE_AUTH_KEY);
      return;
    }
    localStorage.setItem(STORAGE_AUTH_KEY, JSON.stringify(session));
  }

  function getAccessToken() {
    const a = loadAuth();
    return a ? a.access_token : null;
  }

  function sendMagicLink(email, redirectTo) {
    return fetch(SUPABASE_URL + '/auth/v1/otp', {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      // create_user: true (default) — Supabase will create an auth.users row on first login.
      // Non-referrers who get an auth account still see "not_a_referrer" from the dashboard RPC,
      // so this is safe. The referrers table remains the gate for showing any data.
      body: JSON.stringify({
        email: email,
        options: { email_redirect_to: redirectTo || window.location.href },
      }),
    }).then(handleResponse);
  }

  function refreshSession(refreshToken) {
    return fetch(SUPABASE_URL + '/auth/v1/token?grant_type=refresh_token', {
      method: 'POST',
      headers: { apikey: SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).then(handleResponse);
  }

  function captureSessionFromHash() {
    const hash = window.location.hash || '';
    if (hash.indexOf('access_token=') === -1) return null;
    const params = new URLSearchParams(hash.slice(1));
    const session = {
      access_token: params.get('access_token'),
      refresh_token: params.get('refresh_token'),
      expires_in: parseInt(params.get('expires_in') || '3600', 10),
      expires_at: Math.floor(Date.now() / 1000) + parseInt(params.get('expires_in') || '3600', 10),
      token_type: params.get('token_type'),
    };
    if (!session.access_token) return null;
    saveAuth(session);
    // clean the URL
    history.replaceState({}, '', window.location.pathname + window.location.search);
    return session;
  }

  function logout() {
    saveAuth(null);
  }

  // -------------------- Public API --------------------

  const Referral = {
    // Sign-up flow
    checkEligibility: function (email) {
      return rpc('referral_check_eligibility', { p_email: email });
    },
    signup: function (email, phone) {
      return rpc('referral_signup', { p_email: email, p_phone: phone || null });
    },

    // Click tracking
    /**
     * Log a ?ref= click and remember the code in the as_ref cookie.
     *
     * The cookie is written BEFORE the RPC answers. This script is loaded
     * with `defer`, so it runs just ahead of DOMContentLoaded, where the
     * friend page (sections/referral-friend.liquid) reads the cookie to
     * decide whether the visitor was referred. A friend who lands directly
     * on /pages/visit-showroom?ref=CODE must find it there already; waiting
     * for the network answer would make that visit un-referred.
     *
     * Unknown codes (no referrers row) are rejected by the RPC since
     * migration 202610112330 ({success:false, error:'invalid_code'}); the
     * pre-migration RPC answered {success:true, valid_code:false}. Either
     * way the rejected code is then taken back out of the cookie and the
     * value that was there before is restored, so a well-formed foreign
     * ?ref= can't overwrite a real referral (malformed ones such as
     * Judge.me's "judge.me" never get written at all). On a network error
     * or a rate_limited answer the code stays: referral_submit_friend
     * re-validates it server-side, so a best-effort keep is safe.
     *
     * @param {string} code raw ?ref= value
     * @param {object} [opts] unused, kept for API compatibility
     * @returns {Promise<{success: boolean, valid_code?: boolean, error?: string}>}
     */
    recordClick: function (code, opts) {
      opts = opts || {};
      if (!isPlausibleCode(code)) {
        return Promise.resolve({ success: false, error: 'invalid_code', valid_code: false });
      }
      let cookieId = getCookie(COOKIE_NAME + '_id');
      if (!cookieId) {
        cookieId = genCookieId();
        setCookie(COOKIE_NAME + '_id', cookieId, COOKIE_DAYS);
      }
      const previous = getCookie(COOKIE_NAME);
      setCookie(COOKIE_NAME, code, COOKIE_DAYS);
      const undo = function () {
        // Only undo our own write: a later ?ref= click may have replaced it.
        if (getCookie(COOKIE_NAME) !== code) return;
        if (previous && previous !== code) setCookie(COOKIE_NAME, previous, COOKIE_DAYS);
        else clearCookie(COOKIE_NAME);
      };
      const params = new URLSearchParams(window.location.search);
      return rpc('referral_record_click', {
        p_code: code,
        p_cookie_id: cookieId,
        p_landing_page: window.location.href,
        p_referer: document.referrer || null,
        p_user_agent: navigator.userAgent || null,
        p_utm_source: params.get('utm_source'),
        p_utm_medium: params.get('utm_medium'),
        p_utm_campaign: params.get('utm_campaign'),
      }).then((res) => {
        if (isRejectedCode(res)) undo();
        return res;
      });
      // A network / HTTP failure rejects the promise and leaves the cookie
      // as written (see JSDoc).
    },

    // Cookie inspection
    getStoredReferralCode: function () {
      return getCookie(COOKIE_NAME);
    },
    getStoredCookieId: function () {
      return getCookie(COOKIE_NAME + '_id');
    },

    // Friend submitting their info via the referral link
    submitFriend: function (data) {
      return rpc('referral_submit_friend', {
        p_code: data.code || getCookie(COOKIE_NAME),
        p_friend_first_name: data.first_name,
        p_friend_last_name: data.last_name,
        p_friend_email: data.email || null,
        p_friend_phone: data.phone || null,
        p_cookie_id: getCookie(COOKIE_NAME + '_id'),
        p_notes: data.notes || null,
      });
    },

    // Auth + dashboard
    sendMagicLink: sendMagicLink,
    captureSessionFromHash: captureSessionFromHash,
    logout: logout,
    isLoggedIn: function () {
      return !!getAccessToken();
    },
    getMyDashboard: function () {
      return rpc('referral_get_my_dashboard', {});
    },
  };

  // -------------------- Site-wide cookie tracker --------------------
  // Auto-runs on every page load. If ?ref=CODE is present, drop a cookie.

  function autoTrack() {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('ref');
      if (!code) return;
      Referral.recordClick(code).catch(() => {
        /* swallow — tracking is best-effort */
      });
    } catch (e) {
      // swallow
    }
  }

  // -------------------- Boot --------------------

  // Capture auth from URL hash on dashboard return
  if (window.location.hash && window.location.hash.indexOf('access_token=') !== -1) {
    captureSessionFromHash();
  }

  // Auto-track ?ref= on every page
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    autoTrack();
  } else {
    document.addEventListener('DOMContentLoaded', autoTrack);
  }

  // expose
  window.ArcticReferral = Referral;
})();

